import { z } from 'zod';
import type { TFunction } from 'i18next';
import type { DefaultValues } from 'react-hook-form';
import {
  BLOCKER_ANSWERS,
  FEATURE_FREQUENCIES,
  PLATFORM_AREAS,
  SUPPORT_REQUEST_TYPES,
  type PlatformArea,
  type SupportRequest,
} from './support-request.types';

export const MAX_ATTACHMENTS = 5;

export const buildSupportRequestSchema = (t: TFunction<'validation'>) =>
  z
    .object({
      type: z.enum(SUPPORT_REQUEST_TYPES),
      area: z.enum(PLATFORM_AREAS, {
        error: t('supportRequest.supportRequestFields.areaIsRequired'),
      }),
      currentBehavior: z.string().trim(),
      expectedBehavior: z.string().trim(),
      isBlocker: z.enum(BLOCKER_ANSWERS).optional(),
      description: z.string().trim(),
      frequency: z.enum(FEATURE_FREQUENCIES).optional(),
      attachments: z.array(z.instanceof(File)).max(MAX_ATTACHMENTS, {
        error: t('supportRequest.supportRequestFields.tooManyAttachments', {
          count: MAX_ATTACHMENTS,
        }),
      }),
    })
    .superRefine((values, ctx) => {
      if (values.type === 'bug') {
        if (!values.currentBehavior) {
          ctx.addIssue({
            code: 'custom',
            path: ['currentBehavior'],
            message: t('supportRequest.supportRequestFields.currentBehaviorIsRequired'),
          });
        }
        if (!values.isBlocker) {
          ctx.addIssue({
            code: 'custom',
            path: ['isBlocker'],
            message: t('supportRequest.supportRequestFields.blockerAnswerIsRequired'),
          });
        }
      } else {
        if (!values.description) {
          ctx.addIssue({
            code: 'custom',
            path: ['description'],
            message: t('supportRequest.supportRequestFields.descriptionIsRequired'),
          });
        }
        if (values.type === 'feature' && !values.frequency) {
          ctx.addIssue({
            code: 'custom',
            path: ['frequency'],
            message: t('supportRequest.supportRequestFields.frequencyIsRequired'),
          });
        }
      }
    });

export type SupportRequestFormValues = z.infer<ReturnType<typeof buildSupportRequestSchema>>;

export const defaultSupportRequestFormValues: DefaultValues<SupportRequestFormValues> = {
  type: 'bug',
  area: '' as PlatformArea,
  currentBehavior: '',
  expectedBehavior: '',
  description: '',
  attachments: [],
};

export const toSupportRequest = (values: SupportRequestFormValues): SupportRequest => {
  const isBug = values.type === 'bug';
  return {
    type: values.type,
    area: values.area,
    currentBehavior: isBug ? values.currentBehavior : '',
    expectedBehavior: isBug ? values.expectedBehavior : '',
    isBlocker: isBug ? values.isBlocker === 'yes' : null,
    description: isBug ? '' : values.description,
    frequency: values.type === 'feature' ? (values.frequency ?? null) : null,
    attachments: values.attachments,
  };
};
